import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import api from '../API';

const ProjectChromeBrandingContext = createContext({
    resolvedBranding: null,
    setResolvedBranding: () => {},
});

export function useProjectChromeBranding() {
    return useContext(ProjectChromeBrandingContext);
}

export function ProjectChromeBrandingProvider({ children }) {
    const [resolvedBranding, setResolvedBrandingState] = useState(null);
    const setResolvedBranding = useCallback((next) => {
        setResolvedBrandingState(next || null);
    }, []);
    const value = useMemo(
        () => ({ resolvedBranding, setResolvedBranding }),
        [resolvedBranding, setResolvedBranding],
    );
    return (
        <ProjectChromeBrandingContext.Provider value={value}>
            {children}
        </ProjectChromeBrandingContext.Provider>
    );
}

/**
 * Loads resolvedBranding for project-scoped routes with :folderId.
 * Skips /projects/manage and /projects/org (org page sets branding itself).
 */
export function ProjectChromeBrandingSync() {
    const { setResolvedBranding } = useProjectChromeBranding();
    const location = useLocation();
    const params = useParams();
    const folderId = params.folderId;

    useEffect(() => {
        const path = location.pathname || '';
        // Org landing sets chrome branding itself; do not clear or refetch here.
        if (path === '/projects/org' || path.startsWith('/projects/org/')) {
            return undefined;
        }
        // Hub / manage: default BDSA chrome.
        if (path === '/projects' || path.startsWith('/projects/manage')) {
            setResolvedBranding(null);
            return undefined;
        }
        if (!folderId || !path.startsWith('/projects/')) {
            setResolvedBranding(null);
            return undefined;
        }

        let cancelled = false;
        api.get(`projects/${folderId}`, {})
            .then((r) => {
                if (cancelled) return;
                if (r.status === 200 && r.data?.resolvedBranding) {
                    setResolvedBranding(r.data.resolvedBranding);
                } else {
                    setResolvedBranding(null);
                }
            })
            .catch(() => {
                if (!cancelled) setResolvedBranding(null);
            });
        return () => {
            cancelled = true;
        };
    }, [location.pathname, folderId, setResolvedBranding]);

    return null;
}

export default ProjectChromeBrandingContext;
